import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { HeaderErrorEnum } from '../enums/header-error.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import {
    IFile,
    IFileDTOOut,
    IFileElement,
    IFileElementDTOIn,
    IFolderDTOOut,
    IRootFolder,
} from '../interfaces/file-element.interface';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class CloudService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    public getFiles(folderId?: number): Observable<IFileElement[]> {
        const url: string = environment.wasoApi + SubUrlsEnum.BASE_FOLDERS + (folderId ? '/' + folderId : '');
        return this.http
            .get<IFileElementDTOIn[]>(url)
            .pipe(
                map((result: IFileElementDTOIn[]) =>
                    result.map((file: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(file))
                )
            );
    }

    public getRootFolder(): Observable<IFileElement[]> {
        return this.http
            .get<IRootFolder>(environment.wasoApi + SubUrlsEnum.BASE_FOLDERS)
            .pipe(
                map((result: IRootFolder) =>
                    result.folders.map((folder: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(folder))
                )
            );
    }

    public getFolderById(folderId: number): Observable<IFileElement> {
        return this.http
            .get<IFileElementDTOIn>(environment.wasoApi + SubUrlsEnum.FOLDER.replace('{{folderId}}', folderId.toString()))
            .pipe(map((folder: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(folder)));
    }

    public createFolder(parentFolderId: number, newFolder: IFolderDTOOut): Observable<IFileElement> {
        return this.http
            .post<IFileElementDTOIn>(
                environment.wasoApi + SubUrlsEnum.CREATE_FOLDER.replace('{{folderId}}', parentFolderId.toString()),
                newFolder
            )
            .pipe(map((folder: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(folder)));
    }

    public createRootFolder(newFolder: IFolderDTOOut): Observable<IFileElement> {
        return this.http
            .post<IFileElementDTOIn>(environment.wasoApi + SubUrlsEnum.BASE_FOLDERS, newFolder)
            .pipe(map((folder: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(folder)));
    }

    public updateFolder(folderId: number, newFolder: IFolderDTOOut): Observable<IFileElement> {
        return this.http
            .patch<IFileElementDTOIn>(environment.wasoApi + SubUrlsEnum.FOLDER.replace('{{folderId}}', folderId.toString()), newFolder)
            .pipe(map((folder: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(folder)));
    }

    public deleteFolder(folderId: number): Observable<void> {
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.FOLDER.replace('{{folderId}}', folderId.toString()));
    }

    public createFile(parentFolderId: number, newFile: IFileDTOOut): Observable<IFile> {
        const httpOptions: any = {
            headers: new HttpHeaders({
                ['Content-Type']: 'multipart/form-data',
                [HeaderErrorEnum.OWN_ERROR]: 'true',
            }),
        };
        const formData: FormData = new FormData();

        formData.append('item[name]', newFile.name);
        formData.append('item[file]', newFile.file);

        return this.http
            .post<IFile>(environment.wasoApi + SubUrlsEnum.FILES.replace('{{folderId}}', parentFolderId.toString()), formData, httpOptions)
            .pipe(map((file: any) => this.mapperService.fromIFileToInContextIFile(file)));
        // any -> if typed IFile -> error -> map only for file_type
    }

    public updateFile(parentFolderId: number, fileId: number, file: IFileDTOOut): Observable<any> {
        const httpOptions: any = {
            headers: new HttpHeaders({
                ['Content-Type']: 'multipart/form-data',
            }),
        };
        const formData: FormData = new FormData();

        formData.append('item[name]', file.name);

        return this.http
            .patch<any>(
                environment.wasoApi +
                    SubUrlsEnum.FILE.replace('{{folderId}}', parentFolderId.toString()).replace('{{itemId}}', fileId.toString()),
                formData,
                httpOptions
            )
            .pipe(map((updateFile: any) => this.mapperService.fromIFileToInContextIFile(updateFile)));
        // any -> if typed IFile -> error -> map only for file_type
    }

    public deleteFile(parentFolderId: number, fileId: number): Observable<void> {
        return this.http.delete<void>(
            environment.wasoApi +
                SubUrlsEnum.FILE.replace('{{folderId}}', parentFolderId.toString()).replace('{{itemId}}', fileId.toString())
        );
    }

    public searchFiles(searchValue: string): Observable<IFileElement> {
        const params: HttpParams = new HttpParams().set('name', searchValue);
        return this.http
            .get<IFileElementDTOIn>(environment.wasoApi + SubUrlsEnum.SEARCH_FILES /* + (currentFolderId ? '/' + currentFolderId : '')*/, {
                params,
            })
            .pipe(map((folder: IFileElementDTOIn) => this.mapperService.fromIFileElementDTOInToIFileElement(folder)));
    }
}
